/**
 * Terminal SSH in the browser (change fix-egress-and-ssh-terminal D1/D3/D6).
 *
 * The API is the SSH client and the browser is the terminal: PTY bytes travel
 * as binary frames, control travels as JSON. This module is the pure half of
 * that contract — parsing, encoding, theming and the clipboard limits — so the
 * rules can be tested without a DOM, a socket or xterm.
 *
 * The credential never appears in any of it: the browser gets a shell, not a
 * password (SB-4).
 */

import type { SessionTerminal } from "@/lib/terminal-prefs";

/**
 * Control frames the API sends as JSON text (see
 * `apps/api/src/visera_api/sessions/ssh_terminal.py`).
 *
 * - `ready` carries the live PTY geometry and the terminal preferences, so
 *   xterm is configured with the right font and colours before the first paint
 *   instead of after it;
 * - `exit` is the shell ending on its own: the tab closes, no retry;
 * - `error` is a classified reason, never the SSH library's free text.
 */
export type TerminalControl =
  | { type: "ready"; cols: number; rows: number; terminal: SessionTerminal | null }
  | { type: "exit"; code: number; status: number | null }
  | { type: "error"; code: number; reason: string };

const MAX_GEOMETRY = 500;

/**
 * Read a control frame. Returns `null` for anything that is not one of ours:
 * a future frame the browser does not know yet must not be mistaken for a PTY
 * byte stream or kill a working session.
 */
export function parseControl(raw: string): TerminalControl | null {
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof data !== "object" || data === null) return null;
  const frame = data as Record<string, unknown>;
  switch (frame.type) {
    case "ready":
      return {
        type: "ready",
        cols: clampGeometry(frame.cols),
        rows: clampGeometry(frame.rows),
        terminal: readTerminal(frame.terminal),
      };
    case "exit":
      return {
        type: "exit",
        code: typeof frame.code === "number" ? frame.code : 0,
        status: typeof frame.status === "number" ? frame.status : null,
      };
    case "error":
      return {
        type: "error",
        code: typeof frame.code === "number" ? frame.code : 1011,
        reason: typeof frame.reason === "string" ? frame.reason : "error",
      };
    default:
      return null;
  }
}

/**
 * Read a binary WS frame as bytes.
 *
 * `instanceof ArrayBuffer` is not good enough here: a buffer created in another
 * realm (an iframe, a worker, a test's own global) is a perfectly valid PTY
 * chunk and would fail the check and be dropped. So this looks at the shape:
 * no `.buffer` means the buffer itself, a `.buffer` plus a window means a view.
 * A Blob cannot be read synchronously and is unreachable anyway, since the view
 * sets `binaryType = "arraybuffer"`.
 */
export function bytesFrom(data: unknown): Uint8Array | null {
  if (data === null || typeof data !== "object") return null;
  const shape = data as { byteLength?: unknown; byteOffset?: unknown; buffer?: unknown };
  if (typeof shape.byteLength !== "number") return null;
  try {
    if (shape.buffer !== undefined && shape.buffer !== null) {
      const offset = typeof shape.byteOffset === "number" ? shape.byteOffset : 0;
      return new Uint8Array(shape.buffer as ArrayBuffer, offset, shape.byteLength);
    }
    return new Uint8Array(data as ArrayBuffer);
  } catch {
    return null;
  }
}

/**
 * `resize` is the only control frame the browser sends (bytes carry the input).
 * Cols/rows are the PTY's own units, taken from the fit addon.
 */
export function encodeResize(cols: number, rows: number): string {
  return JSON.stringify({ type: "resize", cols, rows });
}

function clampGeometry(value: unknown): number {
  const n = typeof value === "number" && Number.isFinite(value) ? Math.round(value) : 0;
  return Math.min(MAX_GEOMETRY, Math.max(1, n));
}

/**
 * Preferences come back from the server already validated (the POST rejects
 * them with 422), but this is the untrusted edge anyway: a bad scheme falls
 * back to xterm's own default rather than throwing inside the mount effect.
 */
function readTerminal(value: unknown): SessionTerminal | null {
  if (typeof value !== "object" || value === null) return null;
  const prefs = value as Record<string, unknown>;
  const fontSize =
    typeof prefs.font_size === "number" && Number.isFinite(prefs.font_size)
      ? Math.min(72, Math.max(6, Math.round(prefs.font_size)))
      : null;
  const fontName =
    typeof prefs.font_name === "string" && prefs.font_name.length > 0
      ? prefs.font_name.slice(0, 64)
      : null;
  const colorScheme =
    typeof prefs.color_scheme === "string" ? prefs.color_scheme.slice(0, 256) : "";
  if (fontName === null && fontSize === null && colorScheme === "") return null;
  return {
    font_name: fontName ?? "monospace",
    font_size: fontSize ?? 12,
    color_scheme: colorScheme,
  };
}

/**
 * Map the user's colour scheme to an xterm theme.
 *
 * The stored schemes are guacd names (`green-black`, …) because that is what
 * `POST /sessions` validates; `custom` carries a guacd `foreground:/background:`
 * pair. Unknown schemes leave xterm's defaults alone: a scheme nobody
 * recognises must not turn the terminal white on white.
 */
export function themeFor(prefs: SessionTerminal | null): {
  foreground?: string;
  background?: string;
} {
  if (!prefs) return {};
  const scheme = prefs.color_scheme;
  if (!scheme) return {};
  // A custom scheme arrives already expanded by `toSessionTerminal` as a
  // guacd colour pair, not as the literal word "custom".
  if (scheme.includes("foreground:") || scheme.includes("background:")) {
    const fg = guacdColor(scheme, "foreground");
    const bg = guacdColor(scheme, "background");
    return { ...(fg ? { foreground: fg } : {}), ...(bg ? { background: bg } : {}) };
  }
  const preset = PRESETS[scheme];
  return preset ? { ...preset } : {};
}

const PRESETS: Record<string, { foreground: string; background: string }> = {
  "black-white": { foreground: "#111111", background: "#ffffff" },
  "white-black": { foreground: "#f5f5f5", background: "#000000" },
  "gray-black": { foreground: "#b3b3b3", background: "#1c1c1c" },
  "green-black": { foreground: "#33ff66", background: "#000000" },
};

/** `foreground: rgb:ff/ff/ff;` -> `#ffffff`. */
export function guacdColor(scheme: string, key: "foreground" | "background"): string | null {
  const match = new RegExp(`${key}:\\s*rgb:([0-9a-f]{2})/([0-9a-f]{2})/([0-9a-f]{2})`, "i").exec(
    scheme,
  );
  if (!match) return null;
  return `#${match[1]}${match[2]}${match[3]}`.toLowerCase();
}

// --- clipboard (D3) --------------------------------------------------------

/**
 * Same cap the guacd bridge used. The clipboard is untrusted input: a session
 * that selects half a core dump must not be able to hang the tab, and the
 * transferred text never reaches a log, an audit event or a trace (SB-16).
 */
export const CLIPBOARD_MAX_BYTES = 256 * 1024;

export type Capped = { text: string; truncated: boolean };

/** Truncate by UTF-8 bytes, never in the middle of a character. */
export function capText(text: string, max = CLIPBOARD_MAX_BYTES): Capped {
  const encoder = new TextEncoder();
  if (encoder.encode(text).length <= max) return { text, truncated: false };
  let low = 0;
  let high = text.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (encoder.encode(text.slice(0, mid)).length <= max) low = mid;
    else high = mid - 1;
  }
  // A cut in the middle of a grapheme cluster still renders as replacement
  // characters in the terminal; dropping the partial cluster is honest.
  const head = text.slice(0, low);
  const safe = head.replace(/[\uDC00-\uDFFF]?$/u, "");
  return { text: safe, truncated: true };
}

export type PasteKeyEvent = {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey?: boolean;
};

/**
 * The browser's own paste shortcut, with or without Shift (the remote shell
 * gets the raw keysym; xterm turns the text into input itself).
 */
export function isLocalPaste(ev: PasteKeyEvent): boolean {
  return ev.key.toLowerCase() === "v" && (ev.ctrlKey || ev.metaKey) && !ev.altKey;
}

/** Write to the PC clipboard, reporting whether the browser allowed it. */
export async function writeClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Read the PC clipboard; empty or forbidden means "inject nothing". */
export async function readClipboard(): Promise<string | null> {
  try {
    const text = await navigator.clipboard.readText();
    return text.length > 0 ? text : null;
  } catch {
    return null;
  }
}

/**
 * Close codes the API may close a session WS with
 * (`apps/api/src/visera_api/sessions/{router,guacd,ssh_terminal}.py`).
 * 4410/4411 are the SSH handshake ones; the rest are shared with guacd so the
 * browser has a single vocabulary. Every code here has a translated message.
 */
export const SESSION_ERROR_CODES = [
  4401, 4403, 4404, 4405, 4406, 4407, 4408, 4409, 4410, 4411, 1011,
];

export function errorCodeKey(code: number): string | null {
  return SESSION_ERROR_CODES.includes(code) ? `wsError.${code}` : null;
}